import {type RefObject, useCallback, useEffect, useRef} from 'react'

import {throttle} from './throttle'

type Buffer = {x: [number, number]; y: [number, number]}

export type AutoScrollOptions = {
  /** Whether scrolling should be running. On the board this is "a drag is in progress". */
  active: boolean
  /** The container to scroll. */
  scrollRef: RefObject<HTMLElement | null>
  /**
   * Lock scrolling to one axis. The board's root scrolls sideways and each cell scrolls
   * down, so neither of them wants both.
   *
   * A lock also narrows when scrolling happens at all: the pointer has to be inside the
   * container on the axis that is locked out. Cells in a swimlane share that band's
   * vertical extent, so without that rule a pointer near the bottom of one cell is near
   * the bottom of every cell in the row.
   */
  axis?: 'x' | 'y'
  /**
   * Pixels to scroll on each animation frame at full velocity. Also the ceiling: the
   * measured velocity is a fraction of it.
   */
  strength: number
  /**
   * The band at the start and end of the X axis inside which the pointer scrolls the
   * container. Calculated from the container's width when it is not supplied.
   */
  bufferX?: readonly [number, number]
  /** The same band on the Y axis. */
  bufferY?: readonly [number, number]
  /**
   * How much of the container's width stays a non-scrolling dead zone in the middle, as
   * a fraction between 0 and 1, when the X buffer is calculated rather than given.
   */
  deadZoneRatioX?: number
  /** The same, on the Y axis. */
  deadZoneRatioY?: number
  /** Ceiling for a calculated X buffer. */
  maxBufferX?: number
  /** Ceiling for a calculated Y buffer. */
  maxBufferY?: number
  /**
   * Shapes velocity against how far into the buffer the pointer is. Must be stable
   * across renders, or the listeners rebind on every one.
   */
  ease?: (velocity: number) => number
  /** How far the pointer must travel on X before auto-scroll starts. */
  pointerMoveThresholdX?: number
  /** The same, on Y. */
  pointerMoveThresholdY?: number
  /**
   * Insets from the start and end edges of the X axis that are not scrollable content.
   * The board feeds its measured scrollbar thickness in here, so velocity peaks where
   * the content ends rather than where the box does, and a pointer over a scrollbar is
   * treated as being at the edge of the board it is over.
   */
  offsetX?: readonly [number, number]
  /** The same, on Y. */
  offsetY?: readonly [number, number]
}

const PRIMARY_POINTER_BUTTON = 0
const DEFAULT_MAX_BUFFER_X = 300
const DEFAULT_MAX_BUFFER_Y = 200
const DEFAULT_DEAD_ZONE_RATIO = 0.1
const DEFAULT_POINTER_MOVE_THRESHOLD = 15
const BUFFER_MEASURE_INTERVAL_MS = 50
const NO_OFFSET: readonly [number, number] = [0, 0]

type ScrollLoop = {
  scrollRef: RefObject<HTMLElement | null>
  strength: number
  xVelocityRef: RefObject<number | null>
  yVelocityRef: RefObject<number | null>
  animationFrameRef: RefObject<number | null>
}

/**
 * One frame of the scroll loop: move the container, then ask for another frame.
 *
 * Lives at module scope rather than inside the hook because a function that calls itself
 * from within a `useCallback` counts as a dependency of its own memoization scope, which
 * the React Compiler cannot verify against the declared dependency list.
 */
function step(loop: ScrollLoop) {
  const element = loop.scrollRef.current
  if (!element) return

  const yVelocity = loop.yVelocityRef.current
  const xVelocity = loop.xVelocityRef.current
  const {scrollTop, scrollLeft} = element

  if (yVelocity) {
    element.scrollTop = scrollTop + yVelocity * loop.strength
  }

  if (xVelocity) {
    element.scrollLeft = scrollLeft + xVelocity * loop.strength
  }

  loop.animationFrameRef.current = requestAnimationFrame(() => step(loop))
}

/**
 * Scroll a container while the pointer sits in a band at its edge.
 *
 * While `active`, this watches the pointer. When it enters the buffer at an edge, the
 * container scrolls, at a rate set by how far into the buffer the pointer is, the
 * `strength` multiplier and the `ease` function. The loop runs until the pointer leaves
 * every buffer, the mouse is released, or `active` goes false.
 *
 * The drag library's own auto-scroll must be off wherever this is used. Two things
 * scrolling the same container fight each other, and the library's version knows
 * nothing about the axis lock, the dead zones or the offsets.
 *
 * Coordinates arrive as pointer events, so a mouse, a pen and a touch drag all scroll
 * through the same path. A keyboard drag has no pointer and so has no path here at all:
 * it needs whatever moves a card from the keyboard to say which card went where, which is
 * a different input to this hook rather than another listener on it. That is
 * https://github.com/github/views/issues/49, and until it lands a keyboard drag reaches
 * nothing here.
 */
export function useAutoScroll(options: AutoScrollOptions) {
  const {
    active,
    scrollRef,
    axis,
    strength,
    bufferX,
    bufferY,
    ease,
    deadZoneRatioX = DEFAULT_DEAD_ZONE_RATIO,
    deadZoneRatioY = DEFAULT_DEAD_ZONE_RATIO,
    maxBufferX = DEFAULT_MAX_BUFFER_X,
    maxBufferY = DEFAULT_MAX_BUFFER_Y,
    pointerMoveThresholdX = DEFAULT_POINTER_MOVE_THRESHOLD,
    pointerMoveThresholdY = DEFAULT_POINTER_MOVE_THRESHOLD,
    offsetX = NO_OFFSET,
    offsetY = NO_OFFSET,
  } = options

  const animationFrameRef = useRef<number | null>(null)
  const xVelocityRef = useRef<number | null>(null)
  const yVelocityRef = useRef<number | null>(null)
  const pointerOriginRef = useRef<{x: number; y: number} | null>(null)
  const bufferRef = useRef<Buffer>({
    x: [bufferX?.[0] ?? 0, bufferX?.[1] ?? 0],
    y: [bufferY?.[0] ?? 0, bufferY?.[1] ?? 0],
  })

  // Read inside the pointer handler rather than closed over, so a changed offset takes
  // effect without rebinding. The board changes them, because it measures its scrollbars
  // after the first paint.
  const offsetRef = useRef<{x: readonly [number, number]; y: readonly [number, number]}>({x: offsetX, y: offsetY})
  useEffect(() => {
    offsetRef.current = {x: offsetX, y: offsetY}
  }, [offsetX, offsetY])

  const lockedToX = axis === 'x'
  const lockedToY = axis === 'y'

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    // Built here rather than in a memo during render. The throttled function writes to a
    // ref, and building it while rendering makes that a render-time ref access; it also
    // has to be torn down with the observer that drives it, which is this effect's job.
    const updateBuffer = throttle(() => {
      const target = scrollRef.current
      if (!target) return

      const rect = target.getBoundingClientRect()

      // With no buffer supplied, each axis gets half the container minus a dead zone
      // in the middle, capped. The dead zone is what stops a pointer resting in the
      // centre of a short container from scrolling it.
      const defaultBufferX = Math.min(rect.width / 2 - (rect.width * deadZoneRatioX) / 2, maxBufferX)
      const defaultBufferY = Math.min(rect.height / 2 - (rect.height * deadZoneRatioY) / 2, maxBufferY)

      bufferRef.current = {
        x: [bufferX?.[0] ?? defaultBufferX, bufferX?.[1] ?? defaultBufferX],
        y: [bufferY?.[0] ?? defaultBufferY, bufferY?.[1] ?? defaultBufferY],
      }
    }, BUFFER_MEASURE_INTERVAL_MS)

    // Measured from the container rather than from the window: what the buffer depends
    // on is how big this box is, and a board inside a resizable panel changes size
    // without the window ever doing so.
    updateBuffer()
    const observer = new ResizeObserver(() => updateBuffer())
    observer.observe(element)

    return () => {
      observer.disconnect()
      updateBuffer.cancel()
    }
  }, [scrollRef, bufferX, bufferY, deadZoneRatioX, deadZoneRatioY, maxBufferX, maxBufferY])

  const stop = useCallback(() => {
    if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
    animationFrameRef.current = null
    xVelocityRef.current = null
    yVelocityRef.current = null
  }, [])

  // Start the loop: move the container, then ask for another frame. The velocities live
  // in refs, so a pointer that moves mid-loop changes the rate without restarting it.
  // Both are already measured against the axis lock, so nothing here consults it.
  const start = useCallback(() => {
    step({scrollRef, strength, xVelocityRef, yVelocityRef, animationFrameRef})
  }, [scrollRef, strength])

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      // A drag can also be taken away rather than released, by the browser claiming the
      // gesture, which arrives as a cancel and carries no button.
      if (event.type === 'pointerup' && event.button !== PRIMARY_POINTER_BUTTON) return
      stop()
      pointerOriginRef.current = null
    },
    [stop],
  )

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      const container = scrollRef.current
      if (!container) {
        stop()
        return
      }

      // The first move after the drag starts is the origin everything else is measured
      // from. The implementation this was lifted from took it from the press instead,
      // which meant a listener on every instance for the whole life of the page; the
      // board runs one of these per cell, so that cost is not available to it. Measuring
      // from the moment the drag became a drag is also the closer reading of what the
      // threshold is for.
      if (!pointerOriginRef.current) {
        pointerOriginRef.current = {x: event.clientX, y: event.clientY}
        return
      }

      const origin = pointerOriginRef.current
      const rect = container.getBoundingClientRect()
      const {x: xOffsets, y: yOffsets} = offsetRef.current

      // An axis lock also means the pointer has to be inside the container on the other
      // axis. Every cell in a swimlane shares that band's vertical extent, so without
      // this a pointer near the bottom of one cell is near the bottom of all of them,
      // and the whole row scrolls at once.
      const outsideCrossAxis = lockedToY
        ? event.clientX < rect.left + xOffsets[0] || event.clientX > rect.right - xOffsets[1]
        : lockedToX && (event.clientY < rect.top + yOffsets[0] || event.clientY > rect.bottom - yOffsets[1])

      if (outsideCrossAxis) {
        stop()
        return
      }

      // No velocity is measured for an axis the lock forbids moving, or for one the
      // container has no room to move along. Neither is a reason to run: a loop started
      // for an axis that cannot move asks for a frame every frame for the rest of the
      // drag and moves nothing. The overflow test is also what keeps the board's own
      // vertical band silent on a board with no swimlanes, where the single row is the
      // whole scrollport and there is nothing below it to reach.
      //
      // An x-locked board reads the pointer's height for the cross-axis test above and
      // for nothing else. Cells locked to y have the same rule sideways.
      const x =
        lockedToY || !overflowsAlong(container, 'x')
          ? null
          : getBaseVelocity({
              pointerPos: event.clientX,
              containerStart: rect.left,
              containerEnd: rect.right,
              ease,
              buffer: bufferRef.current.x,
              pointerOrigin: origin.x,
              pointerMoveThreshold: pointerMoveThresholdX,
              offset: xOffsets,
            })

      const y =
        lockedToX || !overflowsAlong(container, 'y')
          ? null
          : getBaseVelocity({
              pointerPos: event.clientY,
              containerStart: rect.top,
              containerEnd: rect.bottom,
              ease,
              buffer: bufferRef.current.y,
              pointerOrigin: origin.y,
              pointerMoveThreshold: pointerMoveThresholdY,
              offset: yOffsets,
            })

      // Outside every buffer. Stop rather than schedule a frame that would move nothing:
      // the lifted implementation stopped and then immediately restarted the loop here,
      // leaving an animation frame running for the rest of the drag doing no work.
      if (x === null && y === null) {
        stop()
        return
      }

      xVelocityRef.current = x
      yVelocityRef.current = y

      // Already running: the loop picks the new velocities up from the refs.
      if (animationFrameRef.current === null) start()
    },
    [ease, lockedToX, lockedToY, pointerMoveThresholdX, pointerMoveThresholdY, scrollRef, start, stop],
  )

  useEffect(() => {
    if (!active) return

    // Nothing is bound while the board is idle. A drag can also end without a pointer
    // being released, by being cancelled with Escape or taken over by the browser, so
    // the loop stops on `active` going false and on a cancel as well as on a release.
    pointerOriginRef.current = null
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      stop()
    }
  }, [active, onPointerMove, onPointerUp, stop])
}

/**
 * Velocity along one axis, as a fraction of full strength, or `null` for "do not
 * scroll".
 *
 * Returns `null` until the pointer has travelled `pointerMoveThreshold` from where the
 * drag began, and only scrolls when the pointer is moving towards the edge it is near.
 * Together those stop a card picked up inside a buffer from launching the board across
 * the screen the instant it is lifted.
 *
 * The threshold decides *whether* to scroll and takes no part in how fast. The
 * implementation this was lifted from folded it into the distance as well, in opposite
 * directions on the two edges: the leading edge reached full speed a threshold early,
 * and the trailing edge produced a negative distance for the first threshold of pixels
 * inside its buffer, which scrolled the container backwards, away from the edge the card
 * was being dragged at.
 */
export function getBaseVelocity({
  pointerPos,
  containerStart,
  containerEnd,
  ease,
  buffer,
  pointerOrigin,
  pointerMoveThreshold,
  offset = NO_OFFSET,
}: {
  pointerPos: number
  containerStart: number
  containerEnd: number
  ease: AutoScrollOptions['ease']
  buffer: readonly [number, number]
  pointerOrigin: number
  pointerMoveThreshold: number
  offset?: readonly [number, number]
}): number | null {
  const [bufferStart, bufferEnd] = buffer
  const [offsetStart, offsetEnd] = offset

  // The container's rectangle includes anything that is not scrollable content, such as
  // a scrollbar, so the edges the buffers are measured from are pulled in by the offsets.
  const effectiveContainerStart = containerStart + offsetStart
  const effectiveContainerEnd = containerEnd - offsetEnd

  const startBound = effectiveContainerStart + bufferStart
  const endBound = effectiveContainerEnd - bufferEnd

  if (Math.abs(pointerPos - pointerOrigin) < pointerMoveThreshold) {
    return null
  }

  if (pointerPos < startBound && pointerPos < pointerOrigin) {
    // `Math.max` caps velocity at one whole buffer's worth.
    const distanceIntoBuffer = startBound - Math.max(pointerPos, effectiveContainerStart)
    return applyEasing(-(distanceIntoBuffer / bufferStart), ease)
  }

  if (pointerPos > endBound && pointerPos > pointerOrigin) {
    const distanceIntoBuffer = Math.min(pointerPos, effectiveContainerEnd) - endBound
    return applyEasing(distanceIntoBuffer / bufferEnd, ease)
  }

  return null
}

/**
 * Whether the container has anywhere to go along one axis.
 *
 * Overflowing is the whole test, rather than which direction is left, because velocity
 * already carries the direction and the browser clamps a scroll offset at both ends. What
 * this rules out is an axis with no travel at all, where a loop would ask for a frame
 * every frame and move nothing for the rest of the drag.
 */
function overflowsAlong(container: HTMLElement, axis: 'x' | 'y'): boolean {
  return axis === 'x' ? container.scrollWidth > container.clientWidth : container.scrollHeight > container.clientHeight
}

/**
 * An easing function only has to describe absolute velocity, so direction is stripped
 * before it is called and put back afterwards.
 */
export function applyEasing(velocity: number, ease: AutoScrollOptions['ease']): number {
  if (!ease) return velocity

  return velocity < 0 ? -ease(-velocity) : ease(velocity)
}
